import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import {
  FiAlertTriangle, FiCheckCircle, FiChevronDown, FiChevronRight, FiDownload, FiInfo, FiXCircle,
} from "react-icons/fi";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import {
  Group, Pill, Section, SectionHeader, Skeleton, Widget, WidgetGrid, tokens,
} from "../activity-logs/styles";
import { IOS } from "../activity-logs/labels";
import { CompositionWidget, CreditsPage, Notice, PeriodStats, Tag } from "../creditos/styles";
import { BackButton, ScrolledTopBar } from "../creditos/components/parts";
import { RoundButton } from "../creditos/components/ControlBar";
import { downloadXml, fetchXml } from "../creditos/xmlDocs";
import { tokens as T, hairline } from "../../styles/designTokens";
import { MISS } from "../revisor/home/AnaliseDashboard";
import { displayName } from "../revisor/home/analiseData";
import { branchLabel, formatCount, formatPercent } from "../revisor/home/revisorData";
import { FieldMap, MiniRing } from "./XmlScanHome";
import { PARTIAL } from "./XmlSupplierDirectory";
import { batchDate, buildBatchDetail } from "./xmlScanData";
import { CRT_NAMES, buildDiagnosis, buildSupplierView, isSimples, parseReformaXml } from "./xmlSupplierData";

/**
 * Tela de um fornecedor do XML Scan, no modelo do fornecedor de Créditos.
 * Pergunta da tela: o que exatamente não está conforme e o que fazer?
 * Elemento próprio: o raio X das notas, item a item, lido do XML da amostra.
 * Regras dos dados em `xmlSupplierData.js`.
 */

const CST_COLORS = [T.brand, "#6b9cf0", "#a99af0", "#f2c27a", "#8e8e93"];
const DOC_STATUS = {
  conforme: { label: "Conforme", color: T.brand },
  parcial: { label: "Parcial", color: PARTIAL },
  nao_conforme: { label: "Não conforme", color: MISS },
};
const TONES = {
  ok: { color: T.brand, icon: <FiCheckCircle /> },
  warn: { color: PARTIAL, icon: <FiAlertTriangle /> },
  bad: { color: MISS, icon: <FiXCircle /> },
  flat: { color: "#8e8e93", icon: <FiInfo /> },
};
const INITIAL_ITEMS = 8;
const EMPTY = [];

const money = (v) => (v === null || v === undefined ? "—" : v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));

// ─── XML da amostra ──────────────────────────────────────────────────────────

function useSampleXml(docs) {
  const [byKey, setByKey] = useState({});
  useEffect(() => {
    let alive = true;
    docs.forEach((d) => {
      fetchXml(d.chave)
        .then((xml) => {
          if (!alive) return;
          const data = parseReformaXml(xml);
          setByKey((prev) => ({ ...prev, [d.chave]: data ? { state: "ok", data } : { state: "error" } }));
        })
        .catch(() => { if (alive) setByKey((prev) => ({ ...prev, [d.chave]: { state: "error" } })); });
    });
    return () => { alive = false; };
  }, [docs]);
  return byKey;
}

// ─── Diagnóstico ─────────────────────────────────────────────────────────────

const Verdict = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 14px;
  .ic { flex-shrink: 0; display: flex; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: 50%; font-size: 17px; }
  .t { font-size: 1.05em; font-weight: 600; letter-spacing: -0.02em; line-height: 1.25; }
  .x { margin-top: 4px; font-size: 0.82em; font-weight: 300; line-height: 1.55; color: ${({ theme }) => tokens.label2(theme)}; }
  .n { margin-top: 10px; padding: 9px 12px; border-radius: 10px; font-size: 0.82em; line-height: 1.5; background: rgba(118,118,128,0.1); }
  .n b { font-weight: 600; }
`;

const Path = styled.div`
  margin-top: auto;
  padding-top: 16px;
  .bar { display: flex; gap: 2px; height: 10px; border-radius: 5px; overflow: hidden; background: rgba(118,118,128,0.1); }
  .bar span { height: 100%; }
  .leg { display: flex; flex-wrap: wrap; gap: 6px 22px; margin-top: 10px; font-variant-numeric: tabular-nums; }
  .leg div { display: flex; align-items: baseline; gap: 6px; font-size: 0.8em; color: ${({ theme }) => tokens.label2(theme)}; }
  .leg b { font-size: 1.28em; font-weight: 600; letter-spacing: -0.02em; color: ${({ theme }) => tokens.label(theme)}; }
  .leg i { align-self: center; width: 7px; height: 7px; border-radius: 50%; }
`;

const Facts = styled.div`
  margin-top: 12px;
  div { display: flex; justify-content: space-between; gap: 10px; padding: 7px 0; font-size: 0.8em; border-top: 0.5px solid ${({ theme }) => hairline(theme, true)}; }
  div:first-child { border-top: none; }
  span { color: ${({ theme }) => tokens.label2(theme)}; white-space: nowrap; }
  b { font-weight: 600; text-align: right; font-variant-numeric: tabular-nums; }
`;

const CodeRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
  margin-top: 12px;
  .r { display: grid; grid-template-columns: minmax(0, 1fr) auto; column-gap: 10px; row-gap: 4px; font-size: 0.82em; font-variant-numeric: tabular-nums; }
  .c { min-width: 0; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .c small { margin-right: 6px; font-weight: 600; }
  .n { font-weight: 600; }
  .track { grid-column: 1 / -1; height: 3px; border-radius: 2px; background: rgba(118,118,128,0.1); overflow: hidden; }
  .track span { display: block; height: 100%; border-radius: 2px; }
`;

// ─── Raio X das notas ────────────────────────────────────────────────────────

const DocCard = styled(Group)`
  margin-top: 12px;
  font-variant-numeric: tabular-nums;

  .head { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; padding: 14px 16px; }
  .hmain { flex: 1; min-width: 200px; }
  .hname { display: flex; align-items: center; gap: 10px; font-size: 0.96em; font-weight: 600; letter-spacing: -0.015em; }
  .hsub { margin-top: 3px; font-size: 0.78em; color: ${({ theme }) => tokens.label2(theme)}; }
  .hsum { display: flex; gap: 22px; }
  .hsum div { text-align: right; }
  .hsum b { display: block; font-size: 0.92em; font-weight: 600; letter-spacing: -0.015em; }
  .hsum span { font-size: 0.72em; color: ${({ theme }) => tokens.label2(theme)}; }
  .head-grid, .it { display: grid; grid-template-columns: minmax(0, 1fr) 92px 54px 74px 88px 72px 72px; align-items: center; column-gap: 12px; padding: 0 16px; }
  .head-grid { padding-top: 8px; padding-bottom: 6px; font-size: 0.72em; color: ${({ theme }) => tokens.label3(theme)}; border-top: 0.5px solid ${({ theme }) => hairline(theme, true)}; }
  .head-grid > :nth-child(n+2), .it > :nth-child(n+2) { text-align: right; }
  .it { padding-top: 8px; padding-bottom: 8px; font-size: 0.82em; border-top: 0.5px solid ${({ theme }) => hairline(theme, true)}; }
  .pn { min-width: 0; }
  .pn div { font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .pn span { display: block; margin-top: 1px; font-size: 0.88em; color: ${({ theme }) => tokens.label2(theme)}; }
  .abs { color: ${MISS}; font-weight: 500; }
  .soft { color: ${({ theme }) => tokens.label3(theme)}; }
  .more { display: flex; align-items: center; justify-content: center; gap: 6px; width: 100%; padding: 10px; border: none; border-top: 0.5px solid ${({ theme }) => hairline(theme, true)}; background: none; color: ${T.brand}; font: inherit; font-size: 0.8em; font-weight: 500; cursor: pointer; }
  .state { padding: 0 16px 14px; font-size: 0.8em; font-weight: 300; color: ${({ theme }) => tokens.label2(theme)}; }
  @media (max-width: 720px) {
    .head-grid, .it { grid-template-columns: minmax(0, 1fr) 70px 46px 62px; }
    .head-grid > :nth-child(n+5), .it > :nth-child(n+5) { display: none; }
    .hsum { width: 100%; justify-content: flex-start; }
    .hsum div { text-align: left; }
  }
`;

// Campo ausente: "Falta" quando é exigido; traço discreto quando ainda não era
const FieldCell = ({ value, soft }) => {
  if (value) return <span>{value}</span>;
  return soft ? <span className="soft">—</span> : <span className="abs">Falta</span>;
};

function DocXray({ d, xml }) {
  const [all, setAll] = useState(false);
  const data = xml?.state === "ok" ? xml.data : null;
  const notRequired = d.exempt && (!data || data.withGroup === 0);
  const st = notRequired
    ? { label: "Anterior a 2026", color: "#8e8e93" }
    : DOC_STATUS[d.status] || { label: d.status || "Sem status", color: "#8e8e93" };
  const items = data ? data.items : EMPTY;
  // Itens com problema primeiro
  const ordered = useMemo(() => [...items].sort((a, b) => Number(a.hasGroup && a.base !== null) - Number(b.hasGroup && b.base !== null)), [items]);
  const shown = all ? ordered : ordered.slice(0, INITIAL_ITEMS);
  const without = items.length - (data?.withGroup || 0);
  return (
    <DocCard>
      <div className="head">
        <div className="hmain">
          <div className="hname">Nota {d.numero || "sem número"} <Tag $color={st.color}>{st.label}</Tag></div>
          <div className="hsub" title={d.chave}>
            {d.date ? d.date.toLocaleDateString("pt-BR") : "Sem data"}{data?.natOp ? ` · ${data.natOp}` : ""}{data?.destName ? ` · para ${displayName(data.destName)}` : ""}
          </div>
        </div>
        {data && (
          <div className="hsum">
            <div><b>{money(data.total)}</b><span>Valor da nota</span></div>
            <div><b style={without > 0 && !notRequired ? { color: MISS } : undefined}>{formatCount(without)} de {formatCount(items.length)}</b><span>Itens sem o grupo</span></div>
            {(data.ibs !== null || data.cbs !== null) && <div><b>{money((data.ibs || 0) + (data.cbs || 0))}</b><span>IBS + CBS</span></div>}
          </div>
        )}
        <RoundButton type="button" onClick={() => downloadXml(d.chave)} aria-label={`Baixar XML da nota ${d.numero}`} title="Baixar XML" style={{ width: 30, height: 30 }}><FiDownload /></RoundButton>
      </div>

      {!xml && <div className="state"><Skeleton $h={68} $r={10} /></div>}
      {xml?.state === "error" && (
        <div className="state">
          XML não disponível no projeto. {d.missing.length > 0 ? `Pela análise, faltam ${formatCount(d.missing.filter((m) => !m.endsWith("IS") && m !== "pIS" && m !== "vIS").length)} campos obrigatórios.` : "Pela análise, a nota traz os campos obrigatórios."}
        </div>
      )}
      {data && items.length === 0 && <div className="state">O XML não traz itens de nota.</div>}
      {data && notRequired && items.length > 0 && <div className="state">Nota de {d.date ? d.date.getFullYear() : "antes de 2026"}: o grupo IBS/CBS só é obrigatório a partir de 2026, por isso a análise a considera conforme.</div>}
      {data && items.length > 0 && (
        <>
          <div className="head-grid">
            <span>Item</span><span>Valor</span><span>CST</span><span>cClassTrib</span><span>Base</span><span>IBS</span><span>CBS</span>
          </div>
          {shown.map((it) => (
            <div className="it" key={it.n}>
              <span className="pn">
                <div title={it.name}>{it.name || `Item ${it.n}`}</div>
                <span>NCM {it.ncm || "—"} · CFOP {it.cfop || "—"}</span>
              </span>
              <span>{money(it.value)}</span>
              <FieldCell value={it.cst} soft={notRequired} />
              <FieldCell value={it.cClass} soft={notRequired} />
              <FieldCell value={it.base === null ? "" : money(it.base)} soft={notRequired} />
              <FieldCell value={it.ibs === null ? "" : money(it.ibs)} soft={notRequired} />
              <FieldCell value={it.cbs === null ? "" : money(it.cbs)} soft={notRequired} />
            </div>
          ))}
          {ordered.length > INITIAL_ITEMS && (
            <button type="button" className="more" onClick={() => setAll((v) => !v)}>
              {all ? "Mostrar menos" : `Mostrar os ${formatCount(ordered.length)} itens`} <FiChevronDown style={{ transform: all ? "rotate(180deg)" : "none" }} />
            </button>
          )}
        </>
      )}
    </DocCard>
  );
}

// ─── Tela ────────────────────────────────────────────────────────────────────

export default function XmlSupplierScreen({ supplier, batch: rawBatch, batches = [], onBack, onOpenSupplier }) {
  const theme = useTheme();
  const detail = useMemo(() => buildBatchDetail(rawBatch, batches), [rawBatch, batches]);
  const v = useMemo(() => buildSupplierView(detail, supplier), [detail, supplier]);
  const docs = v ? v.docs : EMPTY;
  const xmlByKey = useSampleXml(docs);
  const docsRef = useRef(null);

  const regime = useMemo(() => {
    const found = docs.map((d) => xmlByKey[d.chave]?.data).filter(Boolean).find((x) => x.crt);
    return found ? { crt: found.crt, uf: found.uf } : null;
  }, [docs, xmlByKey]);

  if (!v) {
    return (
      <CreditsPage>
        <ScrolledTopBar scrollToTop>
          <div className="title-row"><div><BackButton onClick={onBack}>Voltar</BackButton><h1>{displayName(supplier.nome)}</h1></div></div>
        </ScrolledTopBar>
        <Section $first><Notice style={{ marginTop: 0 }}><FiInfo /><span>Este fornecedor não aparece na análise selecionada.</span></Notice></Section>
      </CreditsPage>
    );
  }

  const { s, batch: b } = v;
  const diag = buildDiagnosis(v, regime);
  const tone = TONES[diag.tone];
  const result = [
    { key: "c", label: "Conformes", value: s.conformes, color: T.brand },
    { key: "p", label: "Parciais", value: s.parciais, color: PARTIAL },
    { key: "n", label: "Não conformes", value: s.naoConformes, color: MISS },
  ];
  const pieData = result.filter((x) => x.value > 0);
  const regimeName = regime ? CRT_NAMES[regime.crt] || `CRT ${regime.crt}` : "";
  const cstMax = Math.max(1, v.cst[0]?.value || 0);
  const pct = (val) => (s.total > 0 ? (val / s.total) * 100 : 0);
  const declared = s.total - s.naoConformes;

  return (
    <CreditsPage>
      <ScrolledTopBar scrollToTop>
        <div className="title-row">
          <div style={{ minWidth: 0 }}>
            <BackButton onClick={onBack}>Análise de {batchDate(b, false)}</BackButton>
            <h1 title={s.name}>{displayName(s.name)}</h1>
            <div className="meta">
              {regime && <Pill $color={isSimples(regime.crt) ? IOS.orange : IOS.blue}>{regimeName}</Pill>}
              {v.own && <Pill $color={IOS.indigo}>Própria empresa</Pill>}
              <span className="update-tag">{s.cnpj || "Sem CNPJ"}</span>
              <span className="update-tag">{branchLabel(s.cnpj)}{regime?.uf ? ` · ${regime.uf}` : ""}</span>
            </div>
          </div>
        </div>
      </ScrolledTopBar>

      {/* KPIs */}
      <Section $first>
        <WidgetGrid>
          <Widget $delay={0}>
            <div className="w-head"><FiCheckCircle style={{ color: T.brand, opacity: 1 }} /> Conformidade</div>
            <div className="w-value">{formatPercent(s.pct).replace("%", "")}<small style={{ fontSize: "0.55em", fontWeight: 500, opacity: 0.5, marginLeft: 2 }}>%</small></div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span>{formatCount(s.conformes)} de {formatCount(s.total)} XMLs</span>
              <MiniRing value={s.pct} color={T.brand} />
            </div>
          </Widget>
          <Widget $delay={50}>
            <div className="w-head"><span style={{ width: 7, height: 7, borderRadius: "50%", background: PARTIAL, opacity: 1 }} /> Parciais</div>
            <div className="w-value">{formatCount(s.parciais)}</div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span>{v.rankPartial ? `${formatCount(v.rankPartial)}º da análise` : "Nenhum"}</span>
              <MiniRing value={pct(s.parciais)} color={PARTIAL} />
            </div>
          </Widget>
          <Widget $delay={100}>
            <div className="w-head"><span style={{ width: 7, height: 7, borderRadius: "50%", background: MISS, opacity: 1 }} /> Não conformes</div>
            <div className="w-value">{formatCount(s.naoConformes)}</div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span>{v.rankMissing ? `${formatCount(v.rankMissing)}º da análise` : "Nenhum"}</span>
              <MiniRing value={pct(s.naoConformes)} color={MISS} />
            </div>
          </Widget>
          <Widget $delay={150}>
            <div className="w-head"><FiAlertTriangle style={{ color: IOS.red, opacity: 1 }} /> Alertas críticos</div>
            <div className="w-value">{formatCount(v.critical)}</div>
            <div className="w-foot"><span>{v.critical > 0 ? "itens de nota sem o grupo IBS/CBS" : "Nenhum item sem o grupo"}</span></div>
          </Widget>
        </WidgetGrid>
      </Section>

      {/* Diagnóstico + resultado */}
      <Section $delay={80}>
        <WidgetGrid>
          <Widget $span={3} $delay={200} style={{ display: "flex", flexDirection: "column" }}>
            <div className="w-title">Diagnóstico</div>
            <div className="w-sub">O que está acontecendo com os XMLs deste fornecedor</div>
            <Verdict>
              <span className="ic" style={{ color: tone.color, background: `${tone.color}1f` }}>{tone.icon}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="t">{diag.title}</div>
                <div className="x">{diag.text}</div>
                {diag.next && <div className="n"><b>Próximo passo:</b> {diag.next}</div>}
              </div>
            </Verdict>
            <Path>
              <div className="bar" role="img" aria-label={`${formatPercent(s.pct)} conformes`}>
                {result.filter((x) => x.value > 0).map((x) => <span key={x.key} style={{ flexGrow: x.value, background: x.color, opacity: 0.88 }} />)}
              </div>
              <div className="leg">
                {result.map((x) => <div key={x.key}><i style={{ background: x.color }} /><b>{formatCount(x.value)}</b>{x.label} · {formatPercent(pct(x.value))}</div>)}
              </div>
            </Path>
          </Widget>

          <CompositionWidget $delay={250}>
            <div className="w-title">Posição na análise</div>
            <div className="w-sub">Comparado aos {formatCount(v.supplierCount)} fornecedores</div>
            <div style={{ position: "relative", height: 130, marginTop: 10 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData.length ? pieData : [{ key: "e", value: 1, color: "rgba(118,118,128,0.2)" }]} dataKey="value" innerRadius={48} outerRadius={59} cornerRadius={4} paddingAngle={pieData.length > 1 ? 3 : 0} stroke="none" startAngle={90} endAngle={-270} isAnimationActive={false}>
                    {(pieData.length ? pieData : [{ key: "e", color: "rgba(118,118,128,0.2)" }]).map((x) => <Cell key={x.key} fill={x.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                <b style={{ fontSize: 19, fontWeight: 700, letterSpacing: "-0.03em" }}>{formatPercent(s.pct).replace("%", "")}<span style={{ fontSize: 12, opacity: 0.5, fontWeight: 600 }}>%</span></b>
                <span style={{ fontSize: 10.5, color: tokens.label2(theme) }}>conformes</span>
              </div>
            </div>
            <Facts>
              {v.deltaToBatch !== null && <div><span>Média da análise</span><b>{formatPercent(b.pct)}</b></div>}
              <div><span>Do volume lido</span><b>{formatPercent(v.volumeShare)}</b></div>
              {v.missingShare > 0 && <div><span>Dos não conformes</span><b>{formatPercent(v.missingShare)}</b></div>}
              {v.partialShare > 0 && <div><span>Dos parciais</span><b>{formatPercent(v.partialShare)}</b></div>}
            </Facts>
          </CompositionWidget>
        </WidgetGrid>
      </Section>

      {/* Campos + o que declara */}
      <Section $delay={140}>
        <WidgetGrid>
          <Widget $span={2} $delay={300} style={{ display: "flex", flexDirection: "column" }}>
            <div className="w-title">O que falta, campo a campo</div>
            <div className="w-sub">Quanto mais verde, mais XMLs deste fornecedor trazem o campo. Passe o mouse para ver cada um</div>
            <FieldMap>
              {v.groups.map((g) => (
                <div className="line" key={g.key}>
                  <span className="lbl">{g.label}</span>
                  <div className="cells">
                    {g.fields.map((x) => (
                      <div className="cell" key={x.field} title={`${x.desc}: em ${formatPercent(x.pct)} dos XMLs`}>
                        {x.pct > 0 && <span className="fill" style={{ opacity: 0.12 + (x.pct / 100) * 0.68 }} />}
                        <span className="nm">{x.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </FieldMap>
            <PeriodStats style={{ marginTop: 14 }}>
              <div><span className="v">{formatCount(declared)}</span><span className="l">Com CST e cClassTrib</span></div>
              <div><span className="v">{formatCount(s.conformes)}</span><span className="l">Com todos os valores</span></div>
              <div><span className="v">{formatCount(s.total)}</span><span className="l">XMLs lidos</span></div>
            </PeriodStats>
          </Widget>

          <Widget $delay={350}>
            <div className="w-title">O que declara</div>
            <div className="w-sub">{v.cst.length > 0 ? "CST usado nos XMLs" : "Nenhum CST declarado"}</div>
            {v.cst.length > 0 ? (
              <CodeRows>
                {v.cst.slice(0, 5).map((c, i) => (
                  <div className="r" key={c.code} title={`CST ${c.code}: ${formatCount(c.value)}`}>
                    <span className="c"><small>{c.code}</small>{c.name}</span>
                    <span className="n">{formatCount(c.value)}</span>
                    <span className="track"><span style={{ width: `${(c.value / cstMax) * 100}%`, background: CST_COLORS[i] }} /></span>
                  </div>
                ))}
              </CodeRows>
            ) : <div className="w-sub" style={{ marginTop: 12, fontWeight: 300, lineHeight: 1.5 }}>Nenhum XML traz CST de IBS/CBS, por isso não há classificação para mostrar.</div>}
            {v.classes.length > 0 && (
              <Facts style={{ marginTop: 14 }}>
                {v.classes.slice(0, 4).map((c) => <div key={c.code}><span>cClassTrib {c.code}</span><b>{formatCount(c.value)}</b></div>)}
              </Facts>
            )}
          </Widget>

          <Widget $delay={400}>
            <div className="w-title">Perfil</div>
            <div className="w-sub">Da análise e dos XMLs de amostra</div>
            <Facts>
              <div><span>Regime</span><b>{regimeName || (docs.length > 0 && Object.keys(xmlByKey).length < docs.length ? "Lendo…" : "Não identificado")}</b></div>
              {regime?.uf && <div><span>Estado</span><b>{regime.uf}</b></div>}
              {v.finalities.map((x) => <div key={x.code}><span>{{ 1: "Notas normais", 2: "Complementares", 3: "De ajuste", 4: "Devoluções" }[x.code] || `Finalidade ${x.code}`}</span><b>{formatCount(x.value)}</b></div>)}
              {v.siblings.length > 0 && <div><span>Outros CNPJs do grupo</span><b>{formatCount(v.siblings.length)}</b></div>}
            </Facts>
          </Widget>
        </WidgetGrid>
      </Section>

      {/* Raio X */}
      <Section $delay={200} ref={docsRef}>
        <SectionHeader><h2>Raio X das notas</h2></SectionHeader>
        <Notice style={{ marginTop: 0 }}>
          <FiInfo />
          <span>
            {docs.length === 0
              ? "A API não enviou documentos de amostra deste fornecedor."
              : `Item a item, lido do XML. A API envia uma amostra: ${formatCount(docs.length)} de ${formatCount(s.total)} ${s.total === 1 ? "XML" : "XMLs"} deste fornecedor.`}
          </span>
        </Notice>
        {v.sampleLacksProblem && (
          <Notice>
            <FiAlertTriangle style={{ color: MISS, opacity: 1 }} />
            <span>Nenhum dos {formatCount(s.naoConformes)} XMLs não conformes veio na amostra, então as notas abaixo não mostram o problema deste fornecedor.</span>
          </Notice>
        )}
        {docs.map((d) => <DocXray key={d.chave} d={d} xml={xmlByKey[d.chave]} />)}
      </Section>

      {/* Mesmo grupo */}
      {v.siblings.length > 0 && (
        <Section $delay={240}>
          <SectionHeader><h2>Outros CNPJs do grupo</h2></SectionHeader>
          <Group style={{ padding: "4px 6px" }}>
            {v.siblings.slice(0, 8).map((x) => (
              <SiblingRow key={x.key} type="button" onClick={() => onOpenSupplier(x.raw)} title={x.cnpj}>
                <span className="b">{branchLabel(x.cnpj)}<small>{x.cnpj}</small></span>
                <span className="p">{formatPercent(x.pct)} conformes</span>
                <span className="n">{x.naoConformes > 0 ? `${formatCount(x.naoConformes)} não conformes` : x.parciais > 0 ? `${formatCount(x.parciais)} parciais` : "Tudo conforme"}</span>
                <FiChevronRight />
              </SiblingRow>
            ))}
          </Group>
          {v.siblings.length > 8 && <div className="w-foot" style={{ marginTop: 8, fontSize: "0.78em", color: tokens.label2(theme) }}>E mais {formatCount(v.siblings.length - 8)} CNPJs na aba Fornecedores.</div>}
        </Section>
      )}
    </CreditsPage>
  );
}

const SiblingRow = styled.button`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 130px 140px 14px;
  align-items: center;
  column-gap: 12px;
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  font-variant-numeric: tabular-nums;
  transition: background-color ${T.durBase} ${T.easeOut};
  &:hover { background: rgba(118,118,128,0.08); }
  &:focus-visible { outline: 2px solid ${T.brand}; outline-offset: -2px; }
  .b { min-width: 0; font-size: 0.88em; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .b small { margin-left: 8px; font-size: 0.86em; font-weight: 400; color: ${({ theme }) => tokens.label2(theme)}; }
  .p, .n { text-align: right; font-size: 0.78em; color: ${({ theme }) => tokens.label2(theme)}; white-space: nowrap; }
  svg { font-size: 14px; color: ${({ theme }) => tokens.label3(theme)}; }
  @media (max-width: 720px) { grid-template-columns: minmax(0, 1fr) auto 14px; .p { display: none; } }
`;
